import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import './FillPersonalDetail.scss'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { ApplicationDispatchContext, LOAN_APPLICATION_ACTIONS } from 'contexts/ApplicationContext'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { FormFactory } from 'formBuilder/formFactory'
import { useDispatch, useSelector } from 'react-redux'
import { CustomerForm } from 'constants/enums/formTypes'
import { getLanguageLocale, getMarket } from 'utils/countryHelpers'
import { IApplicationInformation } from 'interfaces/i-application-information'
import {
	FormBuilder,
	formKeySelector,
	validateFormDataAction,
} from 'formBuilder/src'
import updateApplicationServices from 'services/updateApplicationServices'

const SpouseFillPersonalDataLt = () => {
	const { t } = useTranslation()
	const { loanApplication, dispatchLoanApplication }: IApplicationContext =
		useContext(ApplicationDispatchContext)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()
	const applicationId = loanApplication.applicationData?.loanNumber!
	const dispatch = useDispatch();

	let updateSpouseFormSchema = FormFactory(
		CustomerForm.UpdateSpouseForm,
		{
			formName: 'updateSpouseForm',
			blockName: 'updateSpouseForm',
			market: getMarket(),
			locale: getLanguageLocale(),
			getDataUrl: '',
			validationErrorList: false,
			requiredMessage: t('ValidateRequired'),
		},
		loanApplication.applicationData as IApplicationInformation
	)

	const updateSpouseFormStateValues = useSelector(
		formKeySelector(updateSpouseFormSchema.formMeta.formName, 'controlsState')
	)

	const updateSpouseInfo = (ssn: string | undefined, firstName: string | undefined, lastName: string | undefined, mobilePhoneNumber: string | undefined, email: string | undefined
		, monthlyIncome: number | undefined, mortgageMonthlyCost: number | undefined, missedObligations: number | undefined, otherMonthlyCost: number | undefined
		, processDataConsent: boolean | undefined, allowMarketing: boolean | undefined, pep: boolean | undefined,
	) => {
		dispatchLoanApplication({
			type: LOAN_APPLICATION_ACTIONS.SET_UPDATE_APPLICATIONDATA,
			payload: {
				...loanApplication,
				updateApplicationData: {
					...loanApplication.updateApplicationData,
					spouse: {
						...loanApplication.updateApplicationData?.spouse,
						ssn: ssn,
						firstName: firstName,
						lastName: lastName,
						cellPhone: mobilePhoneNumber,
						email: email,
						monthlyIncome: monthlyIncome,
						mortgageMonthlyCost: mortgageMonthlyCost,
						missedObligations: missedObligations,
						otherMonthlyCost: otherMonthlyCost,
						processDataConsent: processDataConsent,
						allowMarketing: allowMarketing,
						pep: pep
					},
				},
			},
		})
	}

	useEffect(
		() => {
			var monthlyIncomeValue = (updateSpouseFormStateValues?.reducedIncome.value ?? false) ? updateSpouseFormStateValues?.expectedFutureIncome.value : updateSpouseFormStateValues?.monthlyIncome.value
			updateSpouseInfo(updateSpouseFormStateValues?.ssn.value, updateSpouseFormStateValues?.firstName.value, updateSpouseFormStateValues?.lastName.value
				,updateSpouseFormStateValues?.mobilePhoneNumber.value, updateSpouseFormStateValues?.email.value, monthlyIncomeValue
				,updateSpouseFormStateValues?.mortgageMonthlyCost.value, updateSpouseFormStateValues?.missedObligations.value, updateSpouseFormStateValues?.otherMonthlyCost.value
				,updateSpouseFormStateValues?.processDataConsent.value, updateSpouseFormStateValues?.allowMarketing.value, updateSpouseFormStateValues?.pep.value
			)
		},
		[updateSpouseFormStateValues] // eslint-disable-line react-hooks/exhaustive-deps
	)

	useEffect(
		() => {
			if(updateSpouseFormStateValues){
				updateSpouseFormStateValues.expectedFutureIncome.visible = updateSpouseFormStateValues.reducedIncome.value
				updateSpouseFormStateValues.expectedFutureIncome.disabled = !updateSpouseFormStateValues.reducedIncome.value
			}
		},
		[updateSpouseFormStateValues?.reducedIncome.value] // eslint-disable-line react-hooks/exhaustive-deps
	)

	const updateSpouseData = async () => {
		dispatch(validateFormDataAction("updateSpouseForm"));
		if(!updateSpouseFormStateValues?.mobilePhoneNumber.isValid || !updateSpouseFormStateValues?.monthlyIncome.isValid
			|| !updateSpouseFormStateValues?.mortgageMonthlyCost.isValid || !updateSpouseFormStateValues?.missedObligations.isValid
			|| !updateSpouseFormStateValues?.otherMonthlyCost.isValid || !updateSpouseFormStateValues?.pep.isValid
			|| !updateSpouseFormStateValues?.processDataConsent.isValid)
		{
			return;
		}

		setLoading(true)
		setError(null)
		try {
			await updateApplicationServices
				.registerSpouse(
					applicationId,
					loanApplication.updateApplicationData!,
					t
				)
				.then(async (response) => {
					dispatchMsp({
						type: MSP_ACTIONS.SET_INDEX,
						payload: {
							index: msp.index + 1,
						},
					})
				})
		} catch (error) {
			console.warn(error)
			setError(error)
			setLoading(false)
		}
	}

	return (
		<div className="update-personal-data">
			{loading ? (
				<div className="col-spinner">
					<CircleSpinner />
				</div>
			) : (
				<>
					{loanApplication.applicationData?.offer === undefined ? (
						<ErrorBox message={`${t('LoanInfoError')}`} />
					) : (
						<>
							{updateSpouseFormSchema ? (
								<>
									<FormBuilder
										formSchema={updateSpouseFormSchema}
										apiError={undefined}
										showErrorsInForm={true}
										showSubmitButton={false}
									/>
									<div className="privacy-policy">
										<div className='privacy-policy-link'>
											<a
												rel="noopener noreferrer"
												target="_blank"
												className="text-link link-darkgray"
												href="https://tfbank.lt/privatumo-politika">
												{t('PrivacyPolicyLinkText')}
											</a>
										</div>
									</div>
								</>
							) : (
								<CircleSpinner />
							)}
							{error ? (
								<ErrorBox message={t('ErrorMsg')} />
							) : null}
							<div className="update-personal-data-next-button">
								<button
									className="btn btn-primary"
									disabled={!updateSpouseFormStateValues?.processDataConsent?.value || !updateSpouseFormStateValues?.privacyPolicy?.value}
									onClick={() => updateSpouseData()}>
									{t('Next')}
								</button>
							</div>
						</>
					)}
				</>
			)}
		</div>
	)
}

export default SpouseFillPersonalDataLt
